import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { errorText } from '../../lib/copy';
import { login } from '../../lib/session';
import styles from './AuthForm.module.css';
import { AuthLayout } from './AuthLayout';
import { takeLoginNotice } from './notice';
import { PasswordField } from './PasswordField';

// Formato, no existencia: la API no dice si un correo tiene cuenta (docs/00-decisiones.md, «Acceso»).
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function LoginPage() {
  const client = useQueryClient();
  const emailId = useId();
  const emailInput = useRef<HTMLInputElement>(null);
  const passwordInput = useRef<HTMLInputElement>(null);
  const [notice] = useState(takeLoginNotice);
  const [step, setStep] = useState<'email' | 'password'>('email');
  // Solo en desarrollo (Vite dev): el entorno de diseño con datos simulados rellena el acceso.
  const devLogin = import.meta.env.DEV
    ? { email: import.meta.env.VITE_DEV_LOGIN_EMAIL ?? '', password: import.meta.env.VITE_DEV_LOGIN_PASSWORD ?? '' }
    : { email: '', password: '' };
  const [email, setEmail] = useState(devLogin.email);
  const [password, setPassword] = useState(devLogin.password);
  const [problem, setProblem] = useState<string | null>(null);
  const mutation = useMutation({ mutationFn: () => login(client, email.trim(), password) });

  // Al cambiar de paso, el foco va al campo que toca rellenar.
  useEffect(() => {
    (step === 'email' ? emailInput : passwordInput).current?.focus();
  }, [step]);

  function continueWithEmail(event: FormEvent) {
    event.preventDefault();
    const value = email.trim();
    if (!value) return setProblem('Escribe tu correo.');
    if (!EMAIL_FORMAT.test(value)) return setProblem('Ese correo no es válido. Revísalo: debe ser como nombre@empresa.com.');
    setProblem(null);
    setEmail(value);
    setStep('password');
  }

  function signIn(event: FormEvent) {
    event.preventDefault();
    if (!password) return setProblem('Escribe tu contraseña.');
    setProblem(null);
    if (!mutation.isPending) mutation.mutate(undefined, { onError: () => passwordInput.current?.select() });
  }

  function changeEmail() {
    mutation.reset();
    setProblem(null);
    setPassword('');
    setStep('email');
  }

  const error = problem ?? (mutation.isError ? errorText(mutation.error) : null);

  return (
    <AuthLayout>
      <p className={styles.eyebrow}>Acceso del personal</p>
      <h1 className={styles.title}>Inicia sesión</h1>
      {notice && <p className={`${styles.notice} ${styles.noticeTop}`} role="status">{notice}</p>}

      {step === 'email' ? (
        <>
          <p className={styles.lead}>Usa el correo de tu cuenta de Hilo.</p>
          <form className={styles.form} onSubmit={continueWithEmail} noValidate>
            <div className={styles.field}>
              <label className={styles.label} htmlFor={emailId}>Correo</label>
              <input ref={emailInput} id={emailId} className={styles.input} type="email" inputMode="email" autoComplete="username"
                autoCapitalize="none" spellCheck={false} value={email} aria-invalid={problem !== null || undefined}
                onChange={(event) => { setEmail(event.target.value); setProblem(null); }} />
            </div>
            {error && <p className={styles.error} role="alert">{error}</p>}
            <button className={`btn btn-primary ${styles.submit}`} type="submit">Continuar</button>
          </form>
        </>
      ) : (
        <>
          <div className={styles.identity} data-initial={email.charAt(0)}>
            <span className={styles.identityLabel}>Entrar como</span>
            <span className={styles.identityEmail}>{email}</span>
            <button type="button" className={styles.change} onClick={changeEmail}>Cambiar</button>
          </div>
          <form className={styles.form} onSubmit={signIn} noValidate>
            {/* Para los gestores de contraseñas: el usuario de este formulario. */}
            <input className="visually-hidden" type="email" autoComplete="username" value={email} readOnly tabIndex={-1} aria-hidden="true" />
            <PasswordField inputRef={passwordInput} label="Contraseña" autoComplete="current-password" value={password}
              onChange={(value) => { setPassword(value); setProblem(null); }}
              invalid={problem !== null || mutation.isError} />
            {error && <p className={styles.error} role="alert">{error}</p>}
            <button className={`btn btn-primary ${styles.submit}`} type="submit" disabled={mutation.isPending} aria-busy={mutation.isPending}>
              {mutation.isPending ? <><span className={styles.spinner} aria-hidden="true" />Entrando…</> : 'Entrar'}
            </button>
          </form>
        </>
      )}

      <p className={styles.help}>
        <strong>¿Olvidaste tu contraseña?</strong> Pide a administración que la restablezca desde Hilo.
        <span className={styles.helpMore}> Recibirás una contraseña temporal y elegirás una nueva al entrar.</span>
      </p>
    </AuthLayout>
  );
}
